/**
 * Favourite-team helpers shared by the match-card/scoreboard star and the profile.
 *
 * Teams live in three `accounts.favourites` stores that together form one favourite set:
 *   - `faviplteams`           curated IPL picker (slugs)
 *   - `favnationalteams`      curated national picker (slugs)
 *   - `favcustomcricketteams` free-form cricket teams starred from matches (display names)
 *
 * Membership is compared by canonical slug (see {@link cricketTeamCanonical}) so a team
 * favourited in any store fills the star everywhere. Also normalises the legacy key names
 * (`favipl`, `favcricketint`, `favfootball`, `favsport`, `favteams`, `favteam1/2`, `favtour`).
 */
import { slugifyCricketTeamName } from "./cricketTeamLogoUrl";
import {
  FAV_TEAM_CATEGORY_KEY,
  canonicalTeamSlug,
  resolveCanonicalTeam,
} from "./cricketTeamCanonical";

export interface Favourites {
  favsports?: string;
  favcricketers?: string;
  faviplteams?: string;
  favnationalteams?: string;
  favfootballteams?: string;
  favcustomcricketteams?: string;
}

/** Canonical key for a team name (comparison/dedup). Mirrors backend `slugifyTeamName`. */
export function slugifyTeamName(name: string): string {
  return slugifyCricketTeamName(name);
}

/** Split a stored comma-separated string into trimmed, non-empty entries. */
export function parseCommaList(raw: string | undefined | null): string[] {
  if (!raw || typeof raw !== "string" || !raw.trim()) return [];
  return raw
    .split(/,\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Parse `favcustomcricketteams` into ordered display names, deduped by canonical slug. */
export function parseFavTeamsFromStorage(raw: string | undefined | null): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const name of parseCommaList(raw)) {
    const slug = canonicalTeamSlug(name);
    if (!slug || seen.has(slug)) continue;
    seen.add(slug);
    out.push(name);
  }
  return out;
}

export function serializeFavTeamsToStorage(names: readonly string[]): string {
  return parseFavTeamsFromStorage(names.join(", ")).join(", ");
}

/**
 * Map any favourites object (legacy or current keys) to the canonical current shape.
 * Legacy single-team fields (`favteam1/2`) and `favteams` fold into `favcustomcricketteams`;
 * `favtour` is dropped.
 */
export function normalizeFavourites(raw: unknown): Favourites {
  if (!raw || typeof raw !== "object") return {};
  const src = raw as Record<string, unknown>;
  const str = (k: string): string | undefined =>
    typeof src[k] === "string" && src[k] !== "" ? (src[k] as string) : undefined;

  const favsports = str("favsports") ?? str("favsport");
  const favcricketers = str("favcricketers");
  const faviplteams = str("faviplteams") ?? str("favipl");
  const favnationalteams = str("favnationalteams") ?? str("favcricketint");
  const favfootballteams = str("favfootballteams") ?? str("favfootball");

  const customParts = [
    str("favcustomcricketteams"),
    str("favcustomteams"),
    str("favteams"),
    str("favteam1"),
    str("favteam2"),
  ]
    .filter((s): s is string => !!s)
    .flatMap((s) => parseCommaList(s));
  const favcustomcricketteams = serializeFavTeamsToStorage(customParts);

  const out: Favourites = {};
  if (favsports) out.favsports = favsports;
  if (favcricketers) out.favcricketers = favcricketers;
  if (faviplteams) out.faviplteams = faviplteams;
  if (favnationalteams) out.favnationalteams = favnationalteams;
  if (favfootballteams) out.favfootballteams = favfootballteams;
  if (favcustomcricketteams) out.favcustomcricketteams = favcustomcricketteams;
  return out;
}

const TEAM_STORE_KEYS: (keyof Favourites)[] = [
  "faviplteams",
  "favnationalteams",
  "favcustomcricketteams",
];

/** Union of canonical slugs across all three team stores. */
export function favouriteTeamSlugSet(f: Favourites | null | undefined): Set<string> {
  const set = new Set<string>();
  if (!f) return set;
  for (const key of TEAM_STORE_KEYS) {
    for (const entry of parseCommaList(f[key])) {
      const slug = canonicalTeamSlug(entry);
      if (slug) set.add(slug);
    }
  }
  return set;
}

/** Is this team a favourite in any store (IPL picker / national picker / custom)? */
export function isFavouriteTeam(f: Favourites | null | undefined, teamName: string): boolean {
  const slug = canonicalTeamSlug(teamName);
  if (!slug) return false;
  return favouriteTeamSlugSet(f).has(slug);
}

function removeBySlug(raw: string | undefined, slug: string): string | undefined {
  const kept = parseCommaList(raw).filter((v) => canonicalTeamSlug(v) !== slug);
  return kept.length ? kept.join(", ") : undefined;
}

function addValue(raw: string | undefined, value: string): string {
  const list = parseCommaList(raw);
  const slug = canonicalTeamSlug(value);
  if (!list.some((v) => canonicalTeamSlug(v) === slug)) list.push(value);
  return list.join(", ");
}

export type FavTeamOp = { key: string; action: "add" | "remove"; value: string };

/**
 * Compute the single-source-of-truth toggle for a team:
 *  - if favourited anywhere, remove it from every store it appears in;
 *  - otherwise add it to its canonical category (IPL / national / custom).
 * Returns the backend ops plus the optimistic next favourites subset.
 */
export function buildFavouriteTeamToggle(
  f: Favourites | null | undefined,
  teamName: string
): { ops: FavTeamOp[]; next: Favourites; favourited: boolean } | null {
  const { slug, category } = resolveCanonicalTeam(teamName);
  if (!slug) return null;

  const next: Favourites = { ...(f ?? {}) };
  const contains = (raw: string | undefined) =>
    parseCommaList(raw).some((v) => canonicalTeamSlug(v) === slug);

  const present: (keyof Favourites)[] = TEAM_STORE_KEYS.filter((k) => contains(next[k]));

  if (present.length > 0) {
    const ops: FavTeamOp[] = present.map((key) => {
      next[key] = removeBySlug(next[key], slug);
      return { key, action: "remove" as const, value: slug };
    });
    return { ops, next, favourited: false };
  }

  const key = FAV_TEAM_CATEGORY_KEY[category] as keyof Favourites;
  const value = category === "custom" ? teamName.trim() : slug;
  next[key] = addValue(next[key], value);
  return { ops: [{ key, action: "add", value }], next, favourited: true };
}
